import { type ApiRequisition } from '@Model';
import {
    type AsyncStateUpdate,
    defineWizardStep,
    isLinked,
    type MetaData,
    type WizardStep,
} from '@synqo/frontend-component-library';
import { type RequisitionWizardState } from '../../RequisitionWizardState';
import { SiteStepBody, type SiteStepErrorKey } from './SiteStepBody';
import { type SiteChangeGuard } from './useSiteChangeGuard';

interface DefineSiteStepProps {
    data: ApiRequisition;
    meta: MetaData<ApiRequisition> | undefined;
    apiResourcePath: string;
    canEdit: boolean;
    /** Asks before the site changes; passed on by a container-aware wizard. */
    siteChangeGuard?: SiteChangeGuard;

    requestUpdateState: AsyncStateUpdate<ApiRequisition>;
    i18n: (key: string) => string;
}

// @ai-generated 2026-07-03T19:15:00Z by dimitri.driediger@synqony.com using Claude Fable 5
export function defineSiteStep(props: DefineSiteStepProps): WizardStep<RequisitionWizardState> {
    const i18n = props.i18n;

    return defineWizardStep<RequisitionWizardState, SiteStepErrorKey>({
        key: 'site',
        label: i18n('Leistungsort'),
        render: () => (
            <SiteStepBody
                data={props.data}
                apiResourcePath={props.apiResourcePath}
                canEdit={props.canEdit}
                siteChangeGuard={props.siteChangeGuard}
                requestUpdateState={props.requestUpdateState}
            />
        ),
        validate: (state) => {
            const errors: Partial<Record<SiteStepErrorKey, string>> = {};
            const address = props.data.site.address;
            if (isLinked(address)) {
                return errors;
            }
            // Snapshot sites come from the "Neue Anschrift anlegen" modal, which enforces the fields.
            const snapshotComplete =
                state.siteUseSnapshot && !!address.label.trim() && !!address.street.trim() && !!address.city.trim();
            if (!snapshotComplete) {
                errors.site = i18n('Bitte wähle einen Leistungsort aus, um fortzufahren.');
            }
            return errors;
        },
        // With a collapsed selection, the secondary button leads back to the picker instead of cancelling.
        secondaryNav: ({ data: feState, update }) => {
            const hasSelection = isLinked(props.data.site.address) || feState.siteUseSnapshot;
            if (feState.sitePicking || !hasSelection) {
                return undefined;
            }
            return { label: i18n('Zurück'), onClick: () => update({ sitePicking: true }) };
        },
    });
}
